import { Container, Graphics, Text, TextStyle } from 'pixi.js';
import { Avatar, type AvatarCosmetics } from './Avatar';

// HP bar dimensions
const BAR_W   = 50;
const BAR_H   = 6;
const BAR_Y   = -80; // relative to entity pivot (above head)
const LABEL_Y = -90;

// Interpolation lerp factor per frame (applied in interpolate())
const LERP_ALPHA = 0.15;

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/**
 * PlayerEntity is a Pixi Container that wraps:
 *  - Avatar child (layered SVG cosmetics)
 *  - Health bar (green → red)
 *  - Name label
 *
 * Works for both the local player and remote players.
 * Remote players: call setTarget() on each server update, then
 * call interpolate() each frame to smooth towards the target.
 */
export class PlayerEntity extends Container {
  private avatar: Avatar;
  private hpBarBg: Graphics;
  private hpBarFg: Graphics;
  private nameLabel: Text;

  private maxHp = 100;
  private currentHp = 100;

  // Remote interpolation
  private targetX: number;
  private targetY: number;

  constructor(
    public readonly playerId: string,
    name = 'Player',
    cosmetics: AvatarCosmetics = {},
    startX = 0,
    startY = 0,
  ) {
    super();

    this.targetX = startX;
    this.targetY = startY;
    this.position.set(startX, startY);

    // ── Avatar ──────────────────────────────────────────────────────────────
    this.avatar = new Avatar(cosmetics);
    // The Avatar pivot is (40, 70) — shift so the feet sit at the entity origin
    this.avatar.position.set(0, -70);
    this.addChild(this.avatar);

    // ── Health bar background (dark) ─────────────────────────────────────────
    this.hpBarBg = new Graphics();
    this.hpBarBg.roundRect(-BAR_W / 2, BAR_Y, BAR_W, BAR_H, 2);
    this.hpBarBg.fill({ color: 0x330000 });
    this.addChild(this.hpBarBg);

    // ── Health bar foreground (coloured fill) ─────────────────────────────────
    this.hpBarFg = new Graphics();
    this.addChild(this.hpBarFg);

    // ── Name label ────────────────────────────────────────────────────────────
    const style = new TextStyle({
      fontFamily: 'Arial',
      fontSize: 11,
      fontWeight: 'bold',
      fill: 0xffffff,
      stroke: { color: 0x000000, width: 3 },
      dropShadow: false,
    });
    this.nameLabel = new Text({ text: name, style });
    this.nameLabel.anchor.set(0.5, 1);
    this.nameLabel.position.set(0, LABEL_Y);
    this.addChild(this.nameLabel);

    // Draw initial bar
    this._redrawBar();
  }

  // ── Avatar loading ─────────────────────────────────────────────────────────

  /**
   * Async: load the Avatar SVG layers. Must be awaited before the entity
   * is visually complete.
   */
  async loadAvatar(): Promise<void> {
    await this.avatar.load();
  }

  // ── HP ─────────────────────────────────────────────────────────────────────

  /**
   * Update the health bar. Redraws the coloured fill immediately.
   * @param hp       current HP
   * @param maxHp    maximum HP (defaults to previous maxHp)
   */
  setHp(hp: number, maxHp?: number): void {
    if (maxHp !== undefined) this.maxHp = maxHp;
    this.currentHp = Math.max(0, Math.min(hp, this.maxHp));
    this._redrawBar();
  }

  private _redrawBar(): void {
    const ratio = this.maxHp > 0 ? this.currentHp / this.maxHp : 0;
    const fillW = Math.max(0, BAR_W * ratio);

    // Colour: green → yellow → red based on HP ratio
    let color: number;
    if (ratio > 0.5) {
      color = 0x44dd44;
    } else if (ratio > 0.25) {
      color = 0xddcc22;
    } else {
      color = 0xdd3333;
    }

    this.hpBarFg.clear();
    if (fillW > 0) {
      this.hpBarFg.roundRect(-BAR_W / 2, BAR_Y, fillW, BAR_H, 2);
      this.hpBarFg.fill({ color });
    }
  }

  // ── Name ───────────────────────────────────────────────────────────────────

  setName(name: string): void {
    this.nameLabel.text = name;
  }

  // ── Aim / facing ───────────────────────────────────────────────────────────

  /**
   * Flip the avatar to face the aim direction.
   * aimAngle is in radians (atan2 from player to target).
   */
  aimAt(aimAngle: number): void {
    // Flip scale.x so character faces left when aiming left
    this.scale.x = Math.cos(aimAngle) < 0 ? -1 : 1;
  }

  // ── Remote player interpolation ────────────────────────────────────────────

  /**
   * Update the target position from a server packet.
   * Call on each server position update (20 tps).
   */
  setTarget(x: number, y: number): void {
    this.targetX = x;
    this.targetY = y;
  }

  /**
   * Lerp current rendered position towards target.
   * Call once per frame from the game loop.
   * @param alpha  0..1, defaults to LERP_ALPHA (0.15)
   */
  interpolate(alpha = LERP_ALPHA): void {
    this.x = lerp(this.x, this.targetX, alpha);
    this.y = lerp(this.y, this.targetY, alpha);
  }

  // ── Expression ─────────────────────────────────────────────────────────────

  async setExpression(
    eyes: Parameters<Avatar['setExpression']>[0],
    mouth: Parameters<Avatar['setExpression']>[1],
  ): Promise<void> {
    await this.avatar.setExpression(eyes, mouth);
  }

  // ── Hit flash ──────────────────────────────────────────────────────────────

  flashHit(): void {
    this.avatar.tint = 0xff4444;
    setTimeout(() => { this.avatar.tint = 0xffffff; }, 120);
  }

  // ── Cleanup ────────────────────────────────────────────────────────────────

  destroyEntity(): void {
    this.avatar.stopIdleAnimation();
    this.destroy({ children: true });
  }
}
